import { AxiosRequestConfig } from 'axios';
import { ICompanyFinancialsDataModel } from '../../data-models/company-financials.data-model';
import {
  ICompanyFundingsRawResponse,
  mapCompanyFundingsResponse,
} from '../../data-models/company-fundings.data-model';
import {
  ICompanyRealIdResponse,
  ICompanySearchResponseDataModel,
} from '../../data-models/company-search-result.data-model';
import {
  CompanyType,
  ICompanyDataModel,
  ICompanyPersonUnlinkDataModel,
} from '../../data-models/company.data-model';
import { INewsDataModel } from '../../data-models/news.data-model';
import { ILocations, IPersonDataModel, IPersonEmailsDataModel } from '../../data-models/person.data-model';
import { ISearchResultDataModel } from '../../data-models/search-result.data-model';
import { MaggieHttp } from '../MaggieHttp';
import { IMetricFundDataModel } from '../../data-models/metrics.data-model';
import {
  IBulkUpdateReportingStatusPayload,
  IBulkUpdateReportingStatusResponse,
} from '../../pages/KPI/components/KPICompanies/hooks/useSetReportingStatus';
import { CompanyMappingPayload, CompanyMappingResponse } from '../../schemas/CompanyMappings.schema';
import { CompanyAuditDataModel } from '../../schemas/CompanyAudit.schema';

export enum SearchType {
  Company = 'company',
  Portfolio = 'portfolio',
  // Person = 'person',
  // Note = 'note',
  Investor = 'investor',
}

export type UpdateCompanyFundPayload = {
  payload: Partial<IMetricFundDataModel>;
  companyFundId: number;
};

export function createCompany(
  companyPayload: Partial<ICompanyDataModel>,
  config?: AxiosRequestConfig<Partial<ICompanyDataModel>>
) {
  const payload = transformCompanyCreationPayload(companyPayload);
  return MaggieHttp.maggie.post<Partial<ICompanyDataModel>, ICompanyDataModel>('/company', payload, config);
}

export async function getRealCompanyId(companyId: string) {
  const { data } = await MaggieHttp.maggie.get<ICompanyRealIdResponse>(`/company/${companyId}/realId`);
  return data;
}

export type UpdateCompanyProps = {
  payload: Partial<ICompanyDataModel>;
  companyId: number;
};
export async function updateCompany({ payload, companyId }: UpdateCompanyProps) {
  const { data } = await MaggieHttp.maggie.put<Partial<ICompanyDataModel>, ICompanyDataModel>(
    `/company/${companyId}`,
    payload
  );
  return data;
}

export async function fetchCompanyRaw(companyId: number) {
  const { data } = await MaggieHttp.maggie.get<ICompanyDataModel>(`company/${companyId}`);
  return data;
}

export interface IPortfolioCompanyBasic {
  id: number;
  name: string;
}

/** Lightweight portfolio list (id + name and a few display fields), permission-scoped server-side. */
export async function fetchPortfolioCompaniesBasic() {
  const { data } = await MaggieHttp.maggie.get<IPortfolioCompanyBasic[]>(`company/portfolio/basic`);
  return data;
}

export async function fetchCompanyNews(companyId: number) {
  const { data } = await MaggieHttp.maggie.get<INewsDataModel[]>(`company/${companyId}/news`);
  return data;
}

export async function fetchCompanyFundings(companyId: number) {
  const { data } = await MaggieHttp.maggie.get<ICompanyFundingsRawResponse | null>(
    `company/${companyId}/fundings`
  );
  return mapCompanyFundingsResponse(data);
}

export async function fetchCompaniesByWebsite(website: string) {
  const { data } = await MaggieHttp.maggie.get<ICompanyDataModel[]>(`/company?website=${website}`);
  return data;
}

// without `to` and `from` params, api returns all available data
export async function fetchCompanyFinancials({
  companyId,
  fromDate,
  toDate,
  currencyCode,
}: {
  companyId: number;
  fromDate?: Date;
  toDate?: Date;
  currencyCode?: string;
}) {
  const params: Record<string, string> = {};
  if (fromDate && toDate) {
    params['from'] = fromDate.toISOString();
    params['to'] = toDate.toISOString();
  }
  if (currencyCode) {
    params['currencyCode'] = currencyCode;
  }

  const { data } = await MaggieHttp.maggie.get<ICompanyFinancialsDataModel[]>(
    `companyFinancials/${companyId}`,
    {
      params,
    }
  );

  return data;
}

// Portfolio companies always rank above discovery/external results, regardless of
// backend relevance scoring — in a client's own environment their portfolio company
// is almost always the intended pick. Stable sort, so backend order is preserved
// within each group.
export function portfolioFirst(
  results: ICompanySearchResponseDataModel[]
): ICompanySearchResponseDataModel[] {
  const rank = (r: ICompanySearchResponseDataModel) => (r.fields?.type === CompanyType.portfolio ? 0 : 1);
  return [...results].sort((a, b) => rank(a) - rank(b));
}

let searchCompaniesController: AbortController | undefined;
export async function searchCompanies(
  searchTerm: string,
  type: SearchType = SearchType.Company,
  abortConsecutive = false
): Promise<ICompanySearchResponseDataModel[]> {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }

  if (searchCompaniesController && abortConsecutive) {
    searchCompaniesController.abort();
  }
  searchCompaniesController = new AbortController();

  try {
    const { data } = await MaggieHttp.maggie.get<ISearchResultDataModel>('/search', {
      params: { text: searchTerm, types: [type] },
      signal: searchCompaniesController.signal,
    });
    const res = data[type] ?? [];

    searchCompaniesController = undefined;

    return portfolioFirst(res);
  } catch (e) {
    console.error(e);
    return [];
  }
}

export async function updateCompanyFund({ payload, companyFundId }: UpdateCompanyFundPayload) {
  const { data } = await MaggieHttp.maggie.put<Partial<IMetricFundDataModel>, IMetricFundDataModel>(
    `/fundCompany/${companyFundId}`,
    payload
  );
  return data;
}

export async function fetchCompanyPeople(companyId: number): Promise<IPersonDataModel[]> {
  const { data } = await MaggieHttp.maggie.get<IPersonDataModel[]>(`/company/${companyId}/people`);
  return data;
}

export async function hidePerson(payload: ICompanyPersonUnlinkDataModel) {
  const { data } = await MaggieHttp.maggie.post(`/personCompanyLink/unlink`, payload);
  return data;
}

export async function unHidePerson(payload: ICompanyPersonUnlinkDataModel) {
  const { data } = await MaggieHttp.maggie.post(`/personCompanyLink/removeUnlink`, payload);
  return data;
}

/**
 * Every person in the tenant.
 *
 * **Unbounded and unpaginated** — `GET /person` returns the whole table with contact
 * details and company associations attached, and it is the only list the API offers. Read
 * it once and cache it hard; do not put it behind anything that re-fetches on interaction.
 */
export async function fetchAllPeople(): Promise<IPersonDataModel[]> {
  const { data } = await MaggieHttp.maggie.get<IPersonDataModel[]>(`/person`);
  return data;
}

/**
 * What the D&O slate's "Add person" form sends. `linkedInUrl` is deliberately absent: the
 * endpoint deduplicates on it, so a blank one collides with the last person entered here.
 */
export interface ICreatePersonInput {
  name: string;
  title?: string;
  primaryEmail?: string;
  /** A column of type `varchar[]`, so a single number still goes as a one-item list. */
  phoneNumbers?: string[];
  /** A `json` column, so the parts go as an object rather than one string to parse. */
  location?: ILocations;
}

/**
 * Creates a person. `POST /person` carries only `AuthGuard`, unlike the legal-structure
 * writes, so the surface gates the button itself rather than relying on the endpoint.
 */
export async function createPerson(input: ICreatePersonInput): Promise<IPersonDataModel> {
  const { data } = await MaggieHttp.maggie.post<ICreatePersonInput, IPersonDataModel>('/person', input);
  return data;
}

export async function fetchPerson(personId: number): Promise<IPersonDataModel> {
  const { data } = await MaggieHttp.maggie.get<IPersonDataModel>(`/person/${personId}`);
  return data;
}

export async function fetchPersonEmails(personId: number): Promise<IPersonEmailsDataModel> {
  const { data } = await MaggieHttp.maggie.get<IPersonEmailsDataModel>(`/person/${personId}/email`);
  return data;
}

export async function fetchAllPortCos() {
  const { data } = await MaggieHttp.maggie.get<ICompanyDataModel[]>(`company/portfolio`);
  return data;
}

export interface IMergeCompanyParams {
  id: number; // old companyId that will be removed
  intoId: number; // newer companyId that we want to keep
}

export async function mergeCompanies({ id, intoId }: IMergeCompanyParams) {
  const { data } = await MaggieHttp.maggie.post(`/company/merge`, { id, intoId });
  return data;
}

function transformCompanyCreationPayload(payload: Partial<ICompanyDataModel>) {
  const { website, ...rest } = payload;

  return {
    website: website ? website : null,
    ...rest,
  };
}

export async function updateGoogleDriveFolder(companyId: number, folderId: string) {
  const { data } = await MaggieHttp.maggie.put(`/google-drive`, { folderId: folderId, companyId });
  return data;
}

export async function bulkUpdateReportingStatus(
  companyIds: number[],
  reporting: boolean
): Promise<IBulkUpdateReportingStatusResponse | void> {
  const res = await MaggieHttp.maggie.put<
    IBulkUpdateReportingStatusPayload,
    IBulkUpdateReportingStatusResponse
  >(`/company/bulk-reporting`, { value: reporting, companyIds });
  return res?.data;
}

export async function getCompanyMappings() {
  const { data } = await MaggieHttp.maggie.get<CompanyMappingResponse[]>('/companyMapping');
  return data;
}

export interface ICompanyMappingUpdateResponse {
  processed: number;
  failures?: { itemId: string; reason: string }[];
}

// TODO: check with BE - can it return the updated items? Otherwise we need to refetch everything on every update
export async function updateCompanyMapping(payload: CompanyMappingPayload[]) {
  const { data } = await MaggieHttp.maggie.post<
    { items: CompanyMappingPayload[] },
    ICompanyMappingUpdateResponse
  >('/companyMapping', { items: payload });
  return data;
}

export async function resolveNonInvestranMappings() {
  const { data } = await MaggieHttp.maggie.post<void, { resolved: number; total: number }>(
    '/companyMapping/resolve-non-investran'
  );
  return data;
}

export async function fetchCompanyAudit(companyId: number, fieldName?: keyof ICompanyDataModel) {
  const { data } = await MaggieHttp.maggie.get<CompanyAuditDataModel[]>(`/company/${companyId}/audit`, {
    params: { fieldName },
  });
  return data;
}

export async function refreshCaptableOwnershipChanges(companyId: number): Promise<void> {
  await MaggieHttp.maggie.post<undefined, void>(`/capTable/refresh-ownership-changes`, undefined, {
    params: { companyId },
  });
}
